import { useState } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Checkbox,
  Stack,
  TableRow,
  TableCell,
  Tooltip,
  Typography,
  MenuItem,
} from '@mui/material';
// @types
import { User } from '../../../../@types/user';
// components
import Label from '../../../../components/Label';
import Iconify from '../../../../components/Iconify';
import { TableMoreMenu } from '../../../../components/table';
import {   useNavigate } from 'react-router';
import { fDate } from '../../../../utils/formatTime';

// ----------------------------------------------------------------------

// Account-creation source → human label + chip colour.
const SOURCE_META: Record<
  string,
  { label: string; color: 'default' | 'info' | 'warning' | 'secondary' | 'success' }
> = {
  direct: { label: 'Direct', color: 'default' },
  admin: { label: 'Admin-created', color: 'info' },
  eduverse: { label: 'Eduverse', color: 'warning' },
  coursera: { label: 'Coursera', color: 'secondary' },
  kaif_community: { label: 'Community', color: 'success' },
};

type Props = {
  row: User;
  selected: boolean;
  // Staff-roles: only full admins get Edit/Delete. Everyone else gets View.
  canWrite?: boolean;
  // customer-care: a single scoped write — assign/remove diplomas.
  canAssignDiploma?: boolean;
  onAssignDiploma?: VoidFunction;
  // Column show/hide: ids of the columns to render. Undefined = show all.
  visibleColumns?: string[];
  // Map of diploma id -> title, to label the Diploma column.
  diplomaMap?: Record<string, string>;
  // Map of course id -> title, to show course NAMES in the Courses column.
  courseMap?: Record<string, string>;
  // Map of track id -> title, to show track NAMES in the Tracks column.
  trackMap?: Record<string, string>;
  onEditRow: VoidFunction;
  onSelectRow: VoidFunction;
  onDeleteRow: VoidFunction;
};

export default function UserTableRow({
  row,
  selected,
  canWrite = true,
  canAssignDiploma = false,
  onAssignDiploma,
  visibleColumns,
  diplomaMap,
  courseMap,
  trackMap,
  onEditRow,
  onSelectRow,
  onDeleteRow,
}: Props) {
  const theme = useTheme();

  const { name, email, avatar, createdOn } = row;
  // Group B (users-list-columns-incomplete): the BE returns more than the
  // template's User type models, so read the extra fields off a loose alias.
  const r = row as any;

  // Courses the student is enrolled in, resolved to NAMES via courseMap (the
  // row only carries course ids). De-duped across tracks; completed ones are
  // flagged so the column can mark them.
  const courseEntries: { id: string; name: string; completed: boolean }[] = [];
  const seenCourseIds = new Set<string>();
  (r.subscribedTracks || []).forEach((t: any) =>
    (t.enrolledCourses || []).forEach((ec: any) => {
      const cid = String(ec?.course?._id || ec?.course || '');
      if (!cid || seenCourseIds.has(cid)) return;
      seenCourseIds.add(cid);
      const populated =
        ec?.course && typeof ec.course === 'object'
          ? ec.course.name || ec.course.title
          : '';
      courseEntries.push({
        id: cid,
        name: (courseMap && courseMap[cid]) || populated || 'Course',
        completed: !!ec?.completed,
      });
    }),
  );

  // Tracks the student is enrolled in (real subscription OR admin grant, since
  // an admin grant also creates a subscribedTracks row), resolved to NAMES via
  // trackMap. Admin-granted tracks are flagged so the column can mark them.
  const assignedTrackIds = new Set<string>(
    ((r.assignedTracks || []) as any[]).map((tk: any) => String(tk?._id ?? tk)),
  );
  const trackEntries: {
    id: string;
    name: string;
    completed: boolean;
    assigned: boolean;
  }[] = [];
  const seenTrackIds = new Set<string>();
  (r.subscribedTracks || []).forEach((t: any) => {
    const tid = String(t?.track?._id || t?.track || '');
    if (!tid || seenTrackIds.has(tid)) return;
    seenTrackIds.add(tid);
    const populated =
      t?.track && typeof t.track === 'object' ? t.track.title || t.track.name : '';
    trackEntries.push({
      id: tid,
      name: (trackMap && trackMap[tid]) || populated || 'Track',
      completed: !!t?.completed,
      assigned: assignedTrackIds.has(tid),
    });
  });
  // Include admin-granted tracks that don't yet have a subscribedTracks row.
  assignedTrackIds.forEach((tid) => {
    if (seenTrackIds.has(tid)) return;
    seenTrackIds.add(tid);
    trackEntries.push({
      id: tid,
      name: (trackMap && trackMap[tid]) || 'Track',
      completed: false,
      assigned: true,
    });
  });

  // Premium is read STRICTLY (`=== true`), the same convention the access check
  // uses, so a stored "true" string cannot read as premium here and as free on
  // the paywall (F-83).
  const isPremium = r.isPremium === true;
  // Warning badges for the two premium populations that need an admin's eye:
  // premium with no end date (the nightly expiry job never reaches them), and
  // premium with no track row (premium that opens nothing by itself).
  const noEndDate = isPremium && !r.subscriptionEndDate;
  const noTrack =
    isPremium && (!Array.isArray(r.subscribedTracks) || r.subscribedTracks.length === 0);

  // Subscription-derived status: Active until subscriptionEndDate, then Expired.
  // Users with no subscription window read as Free.
  const subEnd = r.subscriptionEndDate ? new Date(r.subscriptionEndDate) : null;
  let subStatusLabel: string;
  let subStatusColor: 'success' | 'error' | 'default';
  if (subEnd) {
    if (subEnd.getTime() >= Date.now()) {
      subStatusLabel = 'Active';
      subStatusColor = 'success';
    } else {
      subStatusLabel = 'Expired';
      subStatusColor = 'error';
    }
  } else if (isPremium) {
    // Premium with no stored end date: say so instead of a plain "Active".
    subStatusLabel = 'Active · no end date';
    subStatusColor = 'success';
  } else {
    subStatusLabel = 'Free';
    subStatusColor = 'default';
  }

  // A column is shown when no list is provided (back-compat) or it's included.
  const showCol = (id: string) => !visibleColumns || visibleColumns.includes(id);

  const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpenMenuActions(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpenMenuActions(null);
  };
 let navigate = useNavigate();
  return (
    <TableRow hover selected={selected}>
      <TableCell padding="checkbox">
        <Checkbox checked={selected} onClick={onSelectRow} />
      </TableCell>

      {showCol('Name') && (
        <TableCell>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar alt={name} src={avatar ?? ''} />
            <Typography variant="subtitle2" noWrap>
              {name}
            </Typography>
          </Stack>
        </TableCell>
      )}

      {showCol('Email') && <TableCell align="left"> {email}</TableCell>}
      {showCol('Phone') && <TableCell align="left">{r.phone || '—'}</TableCell>}
      {showCol('Country') && <TableCell align="left">{r.country || '—'}</TableCell>}
      {showCol('Subscription') && (
        <TableCell align="left">
          <Stack spacing={0.5} alignItems="flex-start">
            <Label color={isPremium ? 'success' : 'default'}>
              {isPremium ? row?.package?.name || 'Subscribed' : 'Free'}
            </Label>
            {noEndDate && (
              <Tooltip title="Premium with no subscription end date: the nightly expiry job never ends it. Set one in Edit → Access ends.">
                <span>
                  <Label color="warning" variant="outlined">
                    No end date
                  </Label>
                </span>
              </Tooltip>
            )}
            {noTrack && (
              <Tooltip title="Premium with no track enrolment (no subscribedTracks row).">
                <span>
                  <Label color="warning" variant="outlined">
                    No track
                  </Label>
                </span>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      )}
      {showCol('Verified') && (
        <TableCell align="center">
          <Label color={r.verified ? 'success' : 'warning'}>
            {r.verified ? 'Verified' : 'Unverified'}
          </Label>
        </TableCell>
      )}
      {showCol('Source') && (
        <TableCell align="left">
          <Label color={SOURCE_META[(r as any).source]?.color || 'default'}>
            {SOURCE_META[(r as any).source]?.label || (r as any).source || 'Direct'}
          </Label>
        </TableCell>
      )}
      {showCol('SubStart') && (
        <TableCell align="left">
          {r.subscriptionStartDate ? fDate(new Date(r.subscriptionStartDate)) : '—'}
        </TableCell>
      )}
      {showCol('SubEnd') && (
        <TableCell align="left">
          {r.subscriptionEndDate ? fDate(new Date(r.subscriptionEndDate)) : '—'}
        </TableCell>
      )}

      {showCol('Promo') && (
        <TableCell align="left">
          {r.promoCode ? (
            <Stack spacing={0.25}>
              {/* Partner name (for partner codes) else the source (e.g. admin). */}
              <Typography variant="subtitle2" noWrap sx={{ textTransform: 'capitalize' }}>
                {r.promoSource === 'partner' && r.promoPartnerName
                  ? r.promoPartnerName
                  : r.promoSource || 'admin'}
              </Typography>
              {/* Always show the percentage underneath (admin + partner alike). */}
              <Typography variant="caption" color="text.secondary" noWrap>
                {r.promoDiscount || 0}%
              </Typography>
            </Stack>
          ) : (
            '—'
          )}
        </TableCell>
      )}

      {showCol('Diploma') && (
        <TableCell align="left">
          {(() => {
            const enrollments =
              Array.isArray(r.diplomas) && r.diplomas.length
                ? r.diplomas
                : r.diploma
                ? [{ diploma: r.diploma, batchName: r.batchName }]
                : [];
            if (!enrollments.length) return '—';
            return (
              <Stack spacing={0.75}>
                {enrollments.map((e: any, i: number) => {
                  const did =
                    typeof e.diploma === 'object' ? e.diploma?._id : e.diploma;
                  return (
                    <Stack key={i} spacing={0}>
                      <Typography variant="body2" noWrap>
                        {(diplomaMap && diplomaMap[did]) || 'Diploma'}
                      </Typography>
                      {e.batchName && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {e.batchName}
                        </Typography>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            );
          })()}
        </TableCell>
      )}

      {showCol('Courses') && (
        <TableCell align="left">
          {courseEntries.length === 0 ? (
            '—'
          ) : (
            <Stack spacing={0.25} sx={{ maxWidth: 240 }}>
              {courseEntries.slice(0, 4).map((c) => (
                <Typography
                  key={c.id}
                  variant="body2"
                  noWrap
                  title={c.name}
                  sx={{ color: c.completed ? 'success.main' : 'text.primary' }}
                >
                  {c.completed ? '✓ ' : ''}
                  {c.name}
                </Typography>
              ))}
              {courseEntries.length > 4 && (
                <Typography variant="caption" color="text.secondary">
                  +{courseEntries.length - 4} more
                </Typography>
              )}
            </Stack>
          )}
        </TableCell>
      )}

      {showCol('Tracks') && (
        <TableCell align="left">
          {trackEntries.length === 0 ? (
            '—'
          ) : (
            <Stack spacing={0.25} sx={{ maxWidth: 240 }}>
              {trackEntries.slice(0, 4).map((tk) => (
                <Typography
                  key={tk.id}
                  variant="body2"
                  noWrap
                  title={tk.assigned ? `${tk.name} (assigned)` : tk.name}
                  sx={{ color: tk.completed ? 'success.main' : 'text.primary' }}
                >
                  {tk.completed ? '✓ ' : ''}
                  {tk.name}
                  {tk.assigned ? ' ★' : ''}
                </Typography>
              ))}
              {trackEntries.length > 4 && (
                <Typography variant="caption" color="text.secondary">
                  +{trackEntries.length - 4} more
                </Typography>
              )}
            </Stack>
          )}
        </TableCell>
      )}

      {showCol('status') && (
        <TableCell align="center">
          <Label color={subStatusColor}>{subStatusLabel}</Label>
        </TableCell>
      )}

      <TableCell align="right">
        <TableMoreMenu
          open={openMenu}
          onOpen={handleOpenMenu}
          onClose={handleCloseMenu}
          actions={
            <>
              <MenuItem
                onClick={() => {
                  navigate(`/dashboard/user/account/${row._id}` as string, { replace: true });
                }}
              >
                <Iconify icon={'material-symbols:calendar-view-day-outline'} />
                View
              </MenuItem>
              {/* Diploma-only edit for roles without full Edit rights. */}
              {!canWrite && canAssignDiploma && onAssignDiploma && (
                <MenuItem
                  onClick={() => {
                    onAssignDiploma();
                    handleCloseMenu();
                  }}
                >
                  <Iconify icon={'eva:award-outline'} />
                  Diplomas
                </MenuItem>
              )}
              {canWrite && (
                <MenuItem
                  onClick={() => {
                    onEditRow();
                    handleCloseMenu();
                  }}
                >
                  <Iconify icon={'eva:edit-fill'} />
                  Edit
                </MenuItem>
              )}
              {canWrite && (
                <MenuItem
                  onClick={() => {
                    onDeleteRow();
                    handleCloseMenu();
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <Iconify icon={'eva:trash-2-outline'} />
                  Delete
                </MenuItem>
              )}
            </>
          }
        />
      </TableCell>
    </TableRow>
  );
}
